import React, { useEffect, useState } from "react";
import { Modal } from "@/components/ui";
import { format } from "date-fns";
import API from "@/lib/API";

export const CustomerViewModal = ({ isOpen, onClose, customerData }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && customerData?._id) {
      const fetchActivities = async () => {
        try {
          setLoading(true);
          const res = await API.get(`/customers/${customerData._id}/activities`);
          setActivities(res.data);
        } catch (error) {
          console.error("Error fetching customer activities:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchActivities();
    }
  }, [isOpen, customerData]);

  if (!customerData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="max-w-5xl" title={`Customer Details - ${customerData.sdId || ''}`}>
      <div className="flex flex-col md:flex-row gap-0 p-4">
        {/* Left Side: Info */}
        <div className="w-full md:w-1/2 flex flex-col gap-8 pr-8">
          
          <div>
            <h3 className="text-xs font-semibold text-slate-700 border-b border-slate-100 pb-2 mb-4 uppercase tracking-wider">Basic Information</h3>
            <div className="grid grid-cols-2 gap-y-5 gap-x-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Name</span>
                <span className="text-slate-800">{customerData.customerName || customerData.firstName || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Phone</span>
                <span className="text-slate-800">{customerData.phone || customerData.phoneNumber || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Category</span>
                <span className="text-slate-800">{customerData.category || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Customer Type</span>
                <span className="text-slate-800">{customerData.salesType || customerData.customerType || 'Retail'}</span>
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-400 font-medium">Interested In</span>
                <span className="text-slate-800">{customerData.interestedIn || customerData.projectType || customerData.requirement || '—'}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-slate-700 border-b border-slate-100 pb-2 mb-4 uppercase tracking-wider">Location & Address</h3>
            <div className="grid grid-cols-2 gap-y-5 gap-x-4 text-sm">
              <div className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-400 font-medium">Address Line 1</span>
                <span className="text-slate-800">{customerData.address_line_1 || customerData.addressLine1 || '—'}</span>
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-400 font-medium">Address Line 2</span>
                <span className="text-slate-800">{customerData.address_line_2 || customerData.addressLine2 || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">City</span>
                <span className="text-slate-800">{customerData.city || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">State</span>
                <span className="text-slate-800">{customerData.state || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">PIN Code</span>
                <span className="text-slate-800">{customerData.pin_code || customerData.pincode || customerData.pinCode || '—'}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-xs font-semibold text-slate-700 border-b border-slate-100 pb-2 mb-4 uppercase tracking-wider">Lead Source & Assignment</h3>
            <div className="grid grid-cols-2 gap-y-5 gap-x-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Lead Source</span>
                <span className="text-slate-800">{customerData.referenceType || customerData.source || customerData.leadSource || 'Walk-In'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-400 font-medium">Referred By</span>
                <span className="text-slate-800">{customerData.referred_by || customerData.referredBy || '—'}</span>
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-400 font-medium">Assigned To</span>
                <span className="text-slate-800">
                  {typeof customerData.assignedTo === 'object' && customerData.assignedTo !== null 
                    ? customerData.assignedTo.name || (customerData.assignedTo.firstName ? `${customerData.assignedTo.firstName} ${customerData.assignedTo.lastName || ''}` : null) || customerData.assignedTo.username || 'Unassigned'
                    : (typeof customerData.assignedTo === 'string' && /^[0-9a-fA-F]{24}$/.test(customerData.assignedTo) ? 'Unassigned' : customerData.assignedTo || 'Unassigned')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Timeline */}
        <div className="w-full md:w-1/2 flex flex-col md:border-l md:border-slate-200 pl-0 md:pl-8">
          <div className="pb-4 border-b border-slate-100 mb-4">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Activity Timeline</h3>
          </div>
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-full text-sm text-slate-500">Loading activities...</div>
            ) : activities.length > 0 ? (
              <div className="relative border-l-2 border-slate-200 ml-2 space-y-8 py-2">
                {activities.map((activity, idx) => (
                  <div key={idx} className="relative pl-6">
                    <div className="absolute -left-[9px] top-1 w-4 h-4 bg-[#C91D25] rounded-full border-2 border-white"></div>
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-start gap-4">
                        <span className="text-sm font-medium text-slate-800">{activity.action}</span>
                        <span className="text-[11px] font-medium text-slate-400 whitespace-nowrap">
                          {format(new Date(activity.createdAt), "MMM dd, yyyy HH:mm")}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        By {activity.performedBy?.name || activity.performedBy?.firstName || activity.performedBy?.username || 'System'}
                      </span>
                      {activity.details?.message && (
                        <p className="text-xs text-slate-600 mt-2 bg-slate-50 p-3 rounded-lg border border-slate-100">
                          {activity.details.message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-500">No activities found.</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
